import { call, put, select, takeLatest } from "redux-saga/effects";
import {
  cartFetched,
  orderComplete,
  setLoginMethod,
} from "Store/analytics/analyticsSlice";
import { CART_RESOURCE_ROOT, getResourceRoot } from "Utils/apiUtils";
import {
  removeCookie,
  setEmptyCartCookies,
  updateCookies,
} from "Utils/cookies/cookiesUtil";
import {
  recordCartIfRecording,
  recordOrderIfRecording,
} from "Utils/fixtureRecordingUtils";
import {
  getMergedQueryParams,
  getQueryString,
} from "Utils/navigation/queryParameterUtils";
import { mustRedirectToOfferAccept } from "Utils/navigation/redirectUtil";
import { createRequest, getError } from "Utils/requestUtils";
import { Cart, ConfirmationStatus, OperationType } from "./cartInterfaces";
import {
  acceptOfferAndCreateOrderStart,
  acceptOfferAndSendOrderStart,
  acceptOfferAndValidateOrderStart,
  acceptOfferStart,
  createOrderStart,
  fetchCartStart,
  fetchCartSucceeded,
  operationFailed,
  sendOrderInsuranceRequestStart,
  sendOrderInsuranceRequestSucceeded,
  validateOrderStart,
} from "./cartSlice";

import { getFirstCheckoutStepId } from "Components/Features/CheckOut/checkoutUtils";
import { MainPath } from "Constants/constants";
import { isToggled } from "Store/featureToggles/featureToggleSaga";
import { FeatureToggle } from "Store/featureToggles/featureToggleTypes";
import { updateThemeFromAgreement } from "Store/partners/partnerSlice";
import { partnerFromAgreementCode } from "Store/partners/partnerUtils";
import { ReduxState } from "Store/rootReducer";
import { Login } from "Utils/analytics";
import { getAppPath } from "Utils/appUtils";
import { Cookie } from "Utils/cookies/cookieTypes";
import { isAuthenticated, login } from "Utils/KeycloakWrapper";
import { updateNavbarCartNotification } from "Utils/navbarAndFooter/navbarAndFooterUtils";
import { pushToHistory } from "Utils/navigation/routerUtils";

export const cartSelector = (state: ReduxState): Cart | null =>
  state.cart.current;

export const fetchCartUrl = (
  cartId: string,
  forceRecalculate: boolean,
): string => {
  const url = `${getResourceRoot()}/${cartId}`;
  if (forceRecalculate) {
    const queryParams = new URLSearchParams({ forceRecalculate: "true" });
    return `${url}?${queryParams}`;
  }
  return url;
};

export function* fetchCartFromAction({
  payload: { cartId, forceRecalculate = false },
}: ReturnType<typeof fetchCartStart>): any {
  yield call(fetchCart, cartId, forceRecalculate);
}

export function* fetchCart(cartId: string, forceRecalculate = false): any {
  try {
    const { data } = yield call(createRequest, {
      method: "get",
      url: fetchCartUrl(cartId, forceRecalculate),
      open: !isAuthenticated(),
    });

    const cart = data as Cart;
    if (
      cart.confirmationStatus &&
      cart.confirmationStatus !== ConfirmationStatus.NOT_CONFIRMED
    ) {
      yield put(fetchCartSucceeded(cart));
      yield put(
        operationFailed({
          operationType: OperationType.FETCH_CART,
          response: {
            status: 404,
            detail: ConfirmationStatus.CART_IS_CONFIRMED,
          },
        }),
      );
    } else {
      removeCookie(Cookie.GRP);
      if (cart.cartOwnedByAdvisor) {
        // Make sure that cartId cookie is cleared for all offers
        // as we should only concern ourselves with the cart containing the offer,
        // and never some other previous cart.
        setEmptyCartCookies();
      } else {
        updateCookies(cart);
      }
      recordCartIfRecording(cart);
      yield put(fetchCartSucceeded(cart));
      updateNavbarCartNotification(
        partnerFromAgreementCode(cart.agreementProduct),
      );
      yield put(updateThemeFromAgreement(cart.agreementProduct));
      yield put(cartFetched());
      yield handleRedirectForOffer(cart);
    }
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.FETCH_CART,
        response: getError(e),
      }),
    );
  }
}

function* handleRedirectForOffer(cart: Cart): any {
  const offerAcceptRedirectToggle = yield isToggled(
    FeatureToggle.OFFER_ACCEPT_REDIRECT,
  );
  if (offerAcceptRedirectToggle && mustRedirectToOfferAccept(cart)) {
    // Make sure that cartId cookie is cleared for offers
    // as we should only concern ourselves with the cart containing the offer,
    // and never some other previous cart.
    setEmptyCartCookies();
    pushToHistory(`/${MainPath.OFFER_ACCEPT}/${cart.cartId}`);
  }
}

function* handleRedirectAfterOrder(cart: Cart): any {
  const firstCheckoutStepPath = `${MainPath.CHECKOUT}/${
    cart.cartId
  }/${getFirstCheckoutStepId(cart)}`;
  if (isAuthenticated()) {
    yield put(setLoginMethod(Login.BANK_ID));
    yield put(fetchCartSucceeded(cart));
    pushToHistory(`/${firstCheckoutStepPath}`);
  } else {
    const queryParams = getMergedQueryParams([window.location.search]);
    const redirectUri = `${getAppPath()}/${firstCheckoutStepPath}${getQueryString(
      queryParams,
    )}`;

    login({ redirectUri });
  }
}

export function* createOrder({
  payload,
}: ReturnType<typeof createOrderStart>): any {
  try {
    const { data } = yield call(createRequest, {
      method: "post",
      requestBody: payload,
      url: `${getResourceRoot()}/orders`,
      open: !isAuthenticated(),
    });
    yield put(validateOrderStart((data as Cart).orderId));
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.CREATE_ORDER,
        response: getError(e),
      }),
    );
  }
}

export function* acceptOfferFromAdvisor({
  payload,
}: ReturnType<typeof acceptOfferStart>): any {
  try {
    yield call(createRequest, {
      method: "post",
      requestBody: payload,
      url: `${CART_RESOURCE_ROOT}/accept`,
      open: false,
    });
    yield call(fetchCart, payload.cartId);
    pushToHistory(`/${MainPath.ACCEPT}/${payload.cartId}`);
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.CREATE_ORDER,
        response: getError(e),
      }),
    );
  }
}

export function* acceptOfferAndSendOrder({
  payload,
}: ReturnType<typeof acceptOfferAndSendOrderStart>): any {
  try {
    yield call(createRequest, {
      method: "post",
      requestBody: payload,
      url: `${CART_RESOURCE_ROOT}/accept`,
      open: false,
    });
    yield put(sendOrderInsuranceRequestStart(payload.customer));
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.CREATE_ORDER,
        response: getError(e),
      }),
    );
  }
}

export function* acceptOfferAndValidateOrder({
  payload,
}: ReturnType<typeof acceptOfferAndValidateOrderStart>): any {
  try {
    yield call(createRequest, {
      method: "post",
      requestBody: payload,
      url: `${CART_RESOURCE_ROOT}/accept`,
      open: false,
    });
    yield put(validateOrderStart(payload.orderId));
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.CREATE_ORDER,
        response: getError(e),
      }),
    );
  }
}

export function* acceptOfferAndCreateOrder({
  payload,
}: ReturnType<typeof acceptOfferAndCreateOrderStart>): any {
  try {
    yield call(createRequest, {
      method: "post",
      requestBody: payload,
      url: `${CART_RESOURCE_ROOT}/accept`,
      open: false,
    });
    yield put(createOrderStart(payload));
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.CREATE_ORDER,
        response: getError(e),
      }),
    );
  }
}

export function* validateOrder({
  payload: orderId,
}: ReturnType<typeof validateOrderStart>): any {
  try {
    const { data } = yield call(createRequest, {
      method: "get",
      url: `${getResourceRoot()}/orders/${orderId}/validate`,
      open: !isAuthenticated(),
    });
    yield call(handleRedirectAfterOrder, data as Cart);
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.VALIDATE_ORDER,
        response: getError(e),
      }),
    );
  }
}

export function* sendOrderInsuranceRequest({
  payload: customer,
}: ReturnType<typeof sendOrderInsuranceRequestStart>): any {
  try {
    const cartFromState: Cart = yield select(cartSelector);
    const updatedCart: Cart = { ...cartFromState, customer };
    const { data } = yield call(createRequest, {
      method: "post",
      requestBody: updatedCart,
      url: `${CART_RESOURCE_ROOT}/orders/commit`,
      open: false,
    });

    const cart = data as Cart;
    setEmptyCartCookies();
    recordOrderIfRecording(cart);
    yield put(sendOrderInsuranceRequestSucceeded(cart));

    pushToHistory(`/${MainPath.RECEIPT}/${cart.cartId}`);

    yield put(orderComplete());
  } catch (e) {
    yield put(
      operationFailed({
        operationType: OperationType.COMMIT_ORDER,
        response: getError(e),
      }),
    );
  }
}

export function* cartSaga(): Generator {
  yield takeLatest(fetchCartStart, fetchCartFromAction);
  yield takeLatest(createOrderStart, createOrder);
  yield takeLatest(acceptOfferStart, acceptOfferFromAdvisor);
  yield takeLatest(acceptOfferAndSendOrderStart, acceptOfferAndSendOrder);
  yield takeLatest(
    acceptOfferAndValidateOrderStart,
    acceptOfferAndValidateOrder,
  );
  yield takeLatest(acceptOfferAndCreateOrderStart, acceptOfferAndCreateOrder);
  yield takeLatest(validateOrderStart, validateOrder);
  yield takeLatest(sendOrderInsuranceRequestStart, sendOrderInsuranceRequest);
}
